import React from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { X, Check } from "lucide-react";

export const AMENITIES_CATALOG = [
  "Parking", "Valet parking", "In-house catering", "Kitchen access",
  "Bar / Alcohol license", "AV equipment", "Wi-Fi", "Wheelchair accessible",
  "Outdoor space", "Overnight rooms / lodging", "Dance floor", "Bridal / green suite",
  "Coat check", "Load-in dock", "Public transit access", "Pet-friendly", "Elevator access",
];

export const emptyForm = {
  name: "", google_place_id: null,
  contact_name: "", phone: "", email: "", website: "",
  address_line: "", city: "", region: "", postal_code: "", country: "",
  capacity_seated: "", capacity_standing: "", square_feet: "",
  indoor_outdoor: "indoor",
  amenities: [], amenity_tags: [],
  price_min: "", price_max: "", price_currency: "USD",
  price_notes: "", notes: "",
  is_favorite: false,
};

export const CURRENCIES = ["USD", "EUR", "GBP", "CAD", "AUD", "INR", "MXN", "BRL", "JPY"];

export function formatPriceRange(v) {
  if (!v.price_min && !v.price_max) return "";
  const symbolMap = { USD: "$", EUR: "€", GBP: "£", CAD: "CA$", AUD: "A$", INR: "₹", MXN: "MX$", BRL: "R$", JPY: "¥" };
  const s = symbolMap[v.price_currency || "USD"] || `${v.price_currency || ""} `;
  const fmt = n => (typeof n === "number" ? n.toLocaleString() : "");
  if (v.price_min && v.price_max) return `${s}${fmt(v.price_min)} – ${s}${fmt(v.price_max)}`;
  if (v.price_min) return `From ${s}${fmt(v.price_min)}`;
  return `Up to ${s}${fmt(v.price_max)}`;
}

export function FormBlock({ title, children }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2 pb-1 border-b border-hair">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
export function F({ label, required, children }) {
  return (
    <div>
      <Label className="eyebrow block mb-1.5">{label}{required && <span className="text-[#EF4444] ml-1">*</span>}</Label>
      {children}
    </div>
  );
}
export function Section({ title, children }) {
  return (
    <div className="mb-5">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted mb-2 pb-1 border-b border-hair">{title}</div>
      {children}
    </div>
  );
}
export function MetaCell({ label, value }) {
  return (
    <div className="p-3 border-r border-hair last:border-r-0">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted">{label}</div>
      <div className="font-mono text-base font-semibold tabular-nums text-app mt-0.5 capitalize">{value}</div>
    </div>
  );
}
export function InfoRow({ icon, label }) {
  return <div className="flex items-center gap-2 text-app"><span className="text-muted">{icon}</span><span>{label}</span></div>;
}

export function VenueForm({ form, setForm, tagInput, setTagInput }) {
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: typeof v === "object" && v?.target ? v.target.value : v }));
  const toggleAmenity = (a) => setForm(f => ({ ...f, amenities: f.amenities.includes(a) ? f.amenities.filter(x => x !== a) : [...f.amenities, a] }));
  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!form.amenity_tags.includes(t)) setForm(f => ({ ...f, amenity_tags: [...f.amenity_tags, t] }));
    setTagInput("");
  };
  const removeTag = (t) => setForm(f => ({ ...f, amenity_tags: f.amenity_tags.filter(x => x !== t) }));

  return (
    <div className="space-y-5 py-2">
      <FormBlock title="Basics">
        <F label="Venue name" required><Input value={form.name} onChange={set("name")} data-testid="venue-form-name" autoFocus /></F>
      </FormBlock>
      <FormBlock title="Contact">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <F label="Contact name"><Input value={form.contact_name} onChange={set("contact_name")} data-testid="venue-form-contact-name" /></F>
          <F label="Phone"><Input value={form.phone} onChange={set("phone")} type="tel" data-testid="venue-form-phone" /></F>
          <F label="Email"><Input value={form.email} onChange={set("email")} type="email" data-testid="venue-form-email" /></F>
          <F label="Website"><Input value={form.website} onChange={set("website")} placeholder="https://…" data-testid="venue-form-website" /></F>
        </div>
      </FormBlock>
      <FormBlock title="Address">
        <F label="Street"><Input value={form.address_line} onChange={set("address_line")} data-testid="venue-form-address" /></F>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <F label="City"><Input value={form.city} onChange={set("city")} data-testid="venue-form-city" /></F>
          <F label="State / Region"><Input value={form.region} onChange={set("region")} data-testid="venue-form-region" /></F>
          <F label="Postal code"><Input value={form.postal_code} onChange={set("postal_code")} data-testid="venue-form-postal" /></F>
          <F label="Country"><Input value={form.country} onChange={set("country")} data-testid="venue-form-country" /></F>
        </div>
      </FormBlock>
      <FormBlock title="Capacity & size">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <F label="Seated"><Input type="number" value={form.capacity_seated} onChange={set("capacity_seated")} data-testid="venue-form-capacity-seated" /></F>
          <F label="Standing"><Input type="number" value={form.capacity_standing} onChange={set("capacity_standing")} data-testid="venue-form-capacity-standing" /></F>
          <F label="Size (sq ft)"><Input type="number" value={form.square_feet} onChange={set("square_feet")} data-testid="venue-form-sqft" /></F>
          <F label="Type">
            <Select value={form.indoor_outdoor || "indoor"} onValueChange={set("indoor_outdoor")}>
              <SelectTrigger className="rounded-sm h-10" data-testid="venue-form-indoor-outdoor"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="indoor">Indoor</SelectItem>
                <SelectItem value="outdoor">Outdoor</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
          </F>
        </div>
      </FormBlock>
      <FormBlock title="Price range">
        <div className="grid grid-cols-3 gap-3">
          <F label="Min"><Input type="number" value={form.price_min} onChange={set("price_min")} data-testid="venue-form-price-min" /></F>
          <F label="Max"><Input type="number" value={form.price_max} onChange={set("price_max")} data-testid="venue-form-price-max" /></F>
          <F label="Currency">
            <Select value={form.price_currency || "USD"} onValueChange={set("price_currency")}>
              <SelectTrigger className="rounded-sm h-10" data-testid="venue-form-currency"><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        </div>
        <F label="Pricing notes (optional)"><Textarea rows={2} value={form.price_notes} onChange={set("price_notes")} data-testid="venue-form-price-notes" /></F>
      </FormBlock>
      <FormBlock title="Amenities">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 mb-3">
          {AMENITIES_CATALOG.map(a => {
            const on = form.amenities.includes(a);
            return (
              <button key={a} type="button" onClick={() => toggleAmenity(a)} className={`text-xs px-2 py-1.5 border rounded-sm text-left transition-colors ${on ? "bevel-btn-primary text-white border-[#0A0A0A]" : "bg-surface text-app border-hair hover:border-[#0A0A0A]"}`}>
                {on && <Check size={11} strokeWidth={2} className="inline mr-1" />}{a}
              </button>
            );
          })}
        </div>
        <F label="Custom tags (press Enter)">
          <div className="flex items-center gap-2">
            <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} data-testid="venue-form-tag-input" />
            <Button type="button" variant="outline" onClick={addTag} className="rounded-sm border-hair h-10">Add</Button>
          </div>
          {form.amenity_tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {form.amenity_tags.map(t => (
                <span key={t} className="text-xs px-2 py-1 bevel-btn-primary text-white rounded-sm flex items-center gap-1">
                  #{t}<button type="button" onClick={() => removeTag(t)}><X size={10} strokeWidth={2} /></button>
                </span>
              ))}
            </div>
          )}
        </F>
      </FormBlock>
      <FormBlock title="Notes">
        <F label="General notes (optional)"><Textarea rows={3} value={form.notes} onChange={set("notes")} data-testid="venue-form-notes" /></F>
      </FormBlock>
    </div>
  );
}
